import styled, { css } from "styled-components";
import { colors, font, radius, shadow } from "../../styles/theme/tokens";

// Кольори типів уроків у графіку (вони ж — у легенді)
export const lessonColors = {
  regular: { bg: colors.brandSoft, line: colors.brand, text: colors.brandDark },
  trial: { bg: colors.amberSoft, line: colors.amber, text: "#7A4E00" },
  reschedule: { bg: "#E4F4FB", line: "#3BA9D6", text: "#0F5876" },
  substitute: { bg: "#F0EFF5", line: "#A3A0B8", text: colors.muted },
  cancel: { bg: colors.dangerSoft, line: colors.danger, text: colors.danger },
  slot: { bg: colors.successSoft, line: colors.success, text: colors.success },
};

export const Card = styled.section`
  font-family: ${font.text};
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.card};
  overflow: hidden;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${colors.line};
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.muted};

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 4px;
    background: ${({ $c }) => $c.bg};
    border: 2px solid ${({ $c }) => $c.line};
  }
`;

export const Segment = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: ${radius.pill};
  background: ${colors.bg};
  border: 1px solid ${colors.line};
`;

export const SegmentBtn = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: ${radius.pill};
  font-family: ${font.text};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "#FFFFFF" : colors.muted)};
  background: ${({ $active }) => ($active ? colors.brand : "transparent")};
  transition: background 0.15s;
`;

// Прокрутка всередині картки: шапка днів і колонка часу «липнуть»
export const Scroller = styled.div`
  max-height: 68vh;
  overflow: auto;
  overscroll-behavior: contain;

  @media (max-width: 767px) {
    max-height: 72vh;
  }
`;

export const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: 64px repeat(7, minmax(92px, 1fr));
  min-width: 720px;
`;

export const GridHour = styled.div`
  display: contents;
`;

const sticky = css`
  position: sticky;
  background: ${colors.surface};
  z-index: 3;
`;

export const Corner = styled.div`
  ${sticky};
  top: 0;
  left: 0;
  z-index: 4;
  border-bottom: 1px solid ${colors.line};
`;

export const DayHead = styled.div`
  ${sticky};
  top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 4px;
  border-bottom: 1px solid ${colors.line};
  font-size: 13px;
  font-weight: 700;
  color: ${({ $today }) => ($today ? colors.brandDeep : colors.ink)};

  span {
    font-size: 12px;
    font-weight: 600;
    color: ${({ $today }) => ($today ? colors.brandDeep : colors.muted)};
  }

  ${({ $today }) =>
    $today &&
    css`
      span {
        padding: 1px 8px;
        border-radius: ${radius.pill};
        color: #FFFFFF;
        background: ${colors.brand};
      }
    `}
`;

export const HourLabel = styled.div`
  ${sticky};
  left: 0;
  z-index: 2;
  padding: 6px 10px 0 0;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.muted};
  border-right: 1px solid ${colors.line};
`;

export const GridCell = styled.div`
  position: relative;
  min-height: 56px;
  border-right: 1px solid ${colors.line};
  border-bottom: 1px solid ${colors.line};
  cursor: pointer;
  background: ${({ $today }) => ($today ? "#FBFAFF" : "transparent")};
  transition: background 0.12s;

  &:hover {
    background: ${colors.brandSoft};
  }

  ${({ $slot }) =>
    $slot &&
    css`
      background: ${lessonColors.slot.bg};
      box-shadow: inset 0 0 0 1.5px ${lessonColors.slot.line}55;

      &:hover {
        background: ${lessonColors.slot.bg};
      }
    `}
`;

export const SlotLabel = styled.span`
  position: absolute;
  left: 8px;
  bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${lessonColors.slot.text};
`;

export const LessonCard = styled.div`
  position: absolute;
  left: 4px;
  right: 4px;
  z-index: 1;
  margin-top: 4px;
  padding: 5px 8px;
  border-radius: ${radius.sm};
  border-left: 4px solid ${({ $c }) => $c.line};
  background: ${({ $c }) => $c.bg};
  color: ${({ $c }) => $c.text};
  cursor: ${({ $cancel }) => ($cancel ? "default" : "pointer")};
  box-shadow: 0 1px 2px rgba(30, 27, 58, 0.06);
  transition: transform 0.12s, box-shadow 0.12s;

  &:hover {
    transform: ${({ $cancel }) => ($cancel ? "none" : "translateY(-1px)")};
    box-shadow: ${({ $cancel }) => ($cancel ? "0 1px 2px rgba(30, 27, 58, 0.06)" : "0 6px 14px rgba(30, 27, 58, 0.12)")};
  }

  ${({ $cancel }) =>
    $cancel &&
    css`
      p:first-child {
        text-decoration: line-through;
      }
    `}
`;

export const StudentName = styled.p`
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const LessonTime = styled.p`
  font-size: 11px;
  font-weight: 600;
  opacity: 0.8;
`;

export const DeleteSlotButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: ${colors.muted};
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: #FFFFFF;
    background: ${colors.danger};
  }
`;
